import { useEffect, useMemo, useState } from "react";
import type { AnalyteUpdateResult, Branch } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useDebounced } from "../lib/useQuery.js";
import { type AnalyteDraft, AnalyteFields, blankAnalyte, draftsPayload, emptyFormulaProblem, type IntervalDraft, validateDrafts } from "./CatalogueEditors.js";
import { Alert, Button, Chip, Field, SearchInput } from "./ui.js";

export type StudyChoice = { id: string; clave: string; name: string; is_active: boolean };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * «Nuevo analito» (Catálogo → Analitos; pilot request of 2026-11-03, "crear un nuevo analito o
 * borrar alguno"). The same fields as any analyte, plus the studies it goes into: an analyte is a
 * row of a study, so it needs at least one, and it lands at the end of each list (reorder it there
 * as usual). The clave is checked against the catalogue as it is typed — an existing one, or one
 * removed from the catalogue, is named — and the server checks it again. Each study chosen gets a
 * new version; orders already registered keep the version they were registered with.
 */
export function NewAnalyteForm({ studies, branches, onSaved, onCancel }: { studies: StudyChoice[]; branches: Branch[]; onSaved: (r: AnalyteUpdateResult) => void; onCancel: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<AnalyteDraft>(() => blankAnalyte());
  const [chosen, setChosen] = useState<Set<string>>(() => new Set());
  const [q, setQ] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [taken, setTaken] = useState<string | null>(null);

  const code = draft.code.trim();
  const dCode = useDebounced(code);
  useEffect(() => {
    let live = true;
    setTaken(null);
    if (!dCode) return;
    void get("catalogueAnalytes", { query: { q: dCode, limit: 20, include_deleted: true } })
      .then((page) => {
        const hit = page.items.find((g) => g.code.toUpperCase() === dCode.toUpperCase());
        if (live && hit) setTaken(hit.is_deleted ? `La clave ${hit.code} es de un analito eliminado del catálogo. Búscalo con «Mostrar eliminados» y vuelve a activarlo.` : `Ya existe un analito con la clave ${hit.code}. Para ponerlo en otro estudio, agrégalo desde ese estudio con «Analito de otro estudio».`);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [dCode]);

  // The formula is checked by the server against each chosen study's own analytes (ADR 0023).
  const problems = useMemo(() => new Map([...validateDrafts([draft])].filter(([k]) => !k.endsWith(":formula"))), [draft]);
  const formulaEmpty = emptyFormulaProblem(draft) !== null;
  const patch = (p: Partial<AnalyteDraft>) => setDraft((d) => ({ ...d, ...p }));
  const patchInterval = (rk: string, p: Partial<IntervalDraft>) =>
    setDraft((d) => ({
      ...d,
      intervals: d.intervals.map((r) => {
        if (r.key !== rk) return r;
        const next = { ...r, ...p };
        if (!next.displayTouched && ("low" in p || "high" in p || "expected" in p)) next.display = "";
        return next;
      }),
    }));

  const words = fold(q).split(/\s+/).filter(Boolean);
  const shown = studies.filter((s) => s.is_active && words.every((w) => fold(`${s.clave} ${s.name}`).includes(w)));
  const picked = studies.filter((s) => chosen.has(s.id));
  const toggle = (id: string) =>
    setChosen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const blocking = problems.get(draft.key) ?? (code === "" ? "Escribe la clave del analito." : null) ?? taken ?? (formulaEmpty ? emptyFormulaProblem(draft)!.message : null) ?? (chosen.size === 0 ? "Elige al menos un estudio." : null);

  async function save() {
    const [p] = draftsPayload([draft]);
    setBusy(true);
    try {
      const res = await run<"catalogue.analyte.create", AnalyteUpdateResult>("catalogue.analyte.create", {
        code: p!.code,
        study_ids: picked.map((s) => s.id),
        name: p!.name,
        kind: p!.kind as "numeric" | "text" | "reference" | "document",
        unit: p!.unit,
        decimals: p!.decimals,
        options: p!.options,
        formula: p!.formula,
        branch_formulas: p!.branch_formulas,
        reference_intervals: p!.reference_intervals,
      });
      toast.success(`${res.code} creado en ${plural(res.studies.length, "estudio", "estudios")}.`);
      onSaved(res);
    } catch (e) {
      toast.error(errorMessage(e));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <section className="card stack" style={{ padding: 14, gap: 10 }} aria-label="Analito nuevo">
        <AnalyteFields
          analyte={draft}
          branches={branches}
          problems={problems}
          study={[]}
          formulaProblem={null}
          kinds={["numeric", "text", "reference", "document"]}
          onPatch={patch}
          onPatchInterval={patchInterval}
        />
        {problems.get(draft.key) && <Alert tone="red">{problems.get(draft.key)}</Alert>}
        {taken && <Alert tone="red">{taken}</Alert>}
      </section>

      <section className="stack" style={{ gap: 8 }} aria-label="Agregar a estudios">
        <Field label="Agregar a estudios" hint="Queda al final de la lista de cada estudio; ahí lo puedes mover.">
          <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Clave o nombre del estudio…" aria-label="Buscar estudio" />
        </Field>
        {picked.length > 0 && (
          <div className="row wrap" style={{ gap: 6 }}>
            {picked.map((s) => (
              <button key={s.id} type="button" className="chip blue" aria-label={`Quitar ${s.clave} de la lista`} onClick={() => toggle(s.id)}>
                {s.clave} ×
              </button>
            ))}
          </div>
        )}
        <div className="stack" style={{ gap: 4, maxHeight: 280, overflowY: "auto" }}>
          {shown.slice(0, 80).map((s) => (
            <label key={s.id} className="list-row" style={{ minHeight: 48, gap: 10 }}>
              <input type="checkbox" aria-label={`Agregar a ${s.clave}`} checked={chosen.has(s.id)} onChange={() => toggle(s.id)} style={{ width: 22, height: 22 }} />
              <span className="mono muted" style={{ width: 90 }}>
                {s.clave}
              </span>
              <span className="grow">{s.name}</span>
            </label>
          ))}
          {shown.length === 0 && <div className="faint">Ningún estudio coincide.</div>}
          {shown.length > 80 && <div className="small muted">Se muestran los primeros 80. Escribe para acotar.</div>}
        </div>
      </section>

      {confirming ? (
        <div role="alertdialog" aria-label="Confirmar analito nuevo" className="stack" style={{ gap: 10 }}>
          <Alert tone="blue">
            <strong>
              {code} · {draft.name.trim()} se agregará al final de {plural(picked.length, "estudio", "estudios")}: {picked.map((s) => `${s.clave} · ${s.name}`).join(", ")}.
            </strong>{" "}
            Cada uno queda en una versión nueva. Las órdenes que ya tienen resultados conservan la versión con la que se registraron.
          </Alert>
          <div className="row end">
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Volver
            </Button>
            <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
              Confirmar y crear
            </Button>
          </div>
        </div>
      ) : (
        <div className="row end wrap" style={{ gap: 8 }}>
          {blocking && blocking !== taken && blocking !== problems.get(draft.key) && <span className="small muted grow">{blocking}</span>}
          {chosen.size > 0 && <Chip tone="blue">{plural(chosen.size, "estudio", "estudios")}</Chip>}
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" disabled={blocking !== null} onClick={() => setConfirming(true)}>
            Crear analito
          </Button>
        </div>
      )}
    </div>
  );
}
